import { ControlValueAccessorModel, getProvider } from '../../../Forms/models/control-value-accessor.model';
import { Component, Input } from '@angular/core';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { MailingListsService } from '../../services/mailing-lists.service';
import { ManageMailingListsDialogComponent } from '../ManageMailingListsDialog/manage-mailing-lists-dialog.component';
import { MailingListModel } from '../../models/mailingList.model';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { AutocompleteChipsOptions } from '../../../../Common/Forms/components/AutocompleteChips/autocomplete-chips.component';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../../Core/Dialogs/enums/overlay-animation.enum';

@Component({
    selector: 'agpr-mailing-list-picker',
    templateUrl: './mailing-list-picker.component.html',
    styleUrls: ['./mailing-list-picker.component.scss'],
    providers: [getProvider(MailingListPickerComponent)],
    standalone: false
})
export class MailingListPickerComponent extends ControlValueAccessorModel<number[]> {
    readonly autocompleteOptions: AutocompleteChipsOptions = {
        suggestionsFetcher: this.autocomplete.bind(this),
        delay: 300,
        minLength: 3,
        dropdown: true,
        forceSelection: true
    };

    value: number[] = [];
    subscriptions: Subscription[] = [];

    mailingLists: MailingListModel[] = [];

    allMailingLists: MailingListModel[];

    @Input() placeholder: string = 'Common.mailingLists.picker.placeholder';
    @Input() ariaDescribedBy: string;
    @Input() ariaInvalid: boolean;

    constructor(
        private coreDialogService: CoreDialogService,
        private mailingListsService: MailingListsService
    ) {
        super();

        this.subscriptions.push(
            this.mailingListsService.getMailingLists().subscribe(
                mailingLists => {
                    if (!mailingLists) {
                        return;
                    }
                    this.allMailingLists = mailingLists;
                    this.getListsFromIds();
                }
            )
        );
    }

    writeValue(value: number[]): void {
        super.writeValue(value || []);
        this.getListsFromIds();
    }

    getListsFromIds(): void {
        if (this.allMailingLists) {
            this.mailingLists = this.value.map(id => this.allMailingLists.find(list => list.id === id)).filter(list => !!list);

            if (this.value.length !== this.mailingLists.length) {
                // if a list was deleted, then remove it from the list of lists
                this.updateLists();
            }
        }
    }

    updateLists() {
        this.value = this.mailingLists.map(list => list.id);
        this.onModelChange(this.value);
    }

    autocomplete(query: string): Observable<MailingListModel[]> {
        if (!query) {
            return of(this.allMailingLists.slice());
        } else {
            return of(this.allMailingLists.filter(list => list.name.toLocaleLowerCase().includes(query.toLocaleLowerCase())));
        }
    }

    openManage() {
        const overlayContainer = document.getElementById('main-content');
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );
        const io = {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: overlayContainer
            }
        };
        this.coreDialogService.open(ManageMailingListsDialogComponent, io);
    }

    clearAllChips(allChipsRemoved: boolean): void {
        if (allChipsRemoved) {
            this.mailingLists?.splice(0, this.mailingLists?.length);
            this.updateLists();
        }
    }
}
